'use client';

import { classNames } from '@lib/classNames/classNames';
import { useEffect, useState, useRef } from 'react';

import { LanguageSelectorProps } from './types';

import IconChevron from '@/assets/icons/iconChevron.svg';
import IconLanguage from '@/assets/icons/iconLanguage.svg';
// import { LOCALES } from '@/shared/constants/common';
import { Locale } from '@/shared/types';

const LOCALES = ['ru', 'en', 'kz', 'am'] as Locale[];

const isPageTranslated = (path: string, language: Locale) =>
  !(
    /\/(cases|cityscan|partners|platform-manager)/.test(path) &&
    language !== LOCALES[0]
  );

export const LanguageSelector = ({
  currentLanguage,
  className,
  customClassName,
}: LanguageSelectorProps) => {
  const [isClicked, setIsClicked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const isOpen = isClicked || isHovered;

  useEffect(() => {
    const handleDocumentClick = (event: Event) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setIsClicked(false);
      }
    };
    if (isClicked) {
      document.addEventListener('click', handleDocumentClick);
    }

    return () => document.removeEventListener('click', handleDocumentClick);
  }, [isClicked]);

  const handleLanguageChange = (language: Locale) => {
    setSelectedLanguage(() => language);
    document.cookie = `NEXT_LOCALE=${language}; path=/;`;
    const newHref = `/${language}/${window.location.pathname.replace(new RegExp(`^/${currentLanguage}(/|$)`), '')}${window.location.search}`;

    window.location.href = isPageTranslated(window.location.pathname, language)
      ? newHref
      : '/';
  };

  return (
    <div
      ref={selectorRef}
      className={`relative w-24 ${className}`}
      onMouseLeave={() => setIsHovered(false)}
      onMouseEnter={() => setIsHovered(true)}
    >
      <button
        className="flex w-full justify-center py-2"
        onClick={() => setIsClicked(true)}
        aria-label={currentLanguage}
      >
        <IconLanguage />
        {currentLanguage.toUpperCase()}
        <IconChevron className={classNames('', { 'rotate-180': isOpen })} />
      </button>
      {isOpen && (
        <ul
          className={classNames(
            'absolute z-[101] w-full rounded-20 border border-[1px] border-[#ffffff2e] bg-surfaceBaseBlur backdrop-blur-[2px]',
            {},
            [customClassName?.contentWrapper],
          )}
        >
          {LOCALES.map((language) => (
            <li
              className={classNames('m-2 cursor-pointer text-center', {}, [
                customClassName?.contentItem,
              ])}
              key={language}
              onClick={() => handleLanguageChange(language)}
            >
              {language.toUpperCase()}

              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="ml-1 inline-block"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke={
                    (language === currentLanguage &&
                      language === selectedLanguage) ||
                    language === selectedLanguage
                      ? 'var(--custom-base-inverse-primary)'
                      : '#ffffff2e'
                  }
                  strokeWidth="2"
                  fill="none"
                />
                {((language === currentLanguage &&
                  language === selectedLanguage) ||
                  language === selectedLanguage) && (
                  <circle
                    cx="10"
                    cy="10"
                    r="4"
                    fill="var(--custom-base-inverse-primary)"
                  />
                )}
              </svg>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
